/**
 * HrEmployeeExcelUploadModal 컴포넌트 (V17)
 *
 * V17 변경사항:
 * - 백엔드/프론트엔드 V17 통합 (internal 에러 완전 제거)
 * - 어떤 에러가 발생해도 항상 결과 모달 표시 (인증/권한 에러 제외)
 * - 결과 불변식: totalCount === successCount + failureCount 항상 보장
 * - 청크별 상세 로깅 지원 (Firebase Functions 로그에서 확인)
 *
 * V14 변경사항:
 * - 백엔드 V14 안정화에 맞춰 에러 처리 강화
 * - "internal 에러" 대신 항상 BulkImportResult 표시 보장
 *
 * **역할:**
 * - 엑셀 파일 업로드로 직원 여러 명 추가
 * - 엑셀 파싱 후 Cloud Function 호출
 * - 성공/실패 결과 표시
 */

import React, { useState } from 'react';
import { X, Upload, FileSpreadsheet, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  useEmployeeCreateMutations,
  BulkImportResult,
} from '../../../shared/hooks/useEmployeeCreateMutations';
import { useModalOverlayClose } from '../../../shared/hooks/useModalOverlayClose';

/**
 * HrEmployeeExcelUploadModal Props
 */
export interface HrEmployeeExcelUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * HrEmployeeExcelUploadModal 컴포넌트
 */
export function HrEmployeeExcelUploadModal({
  isOpen,
  onClose,
  onSuccess,
}: HrEmployeeExcelUploadModalProps) {
  // ============================================
  // 상태 및 Hook (V14: importResult와 importError 분리)
  // ============================================

  const { bulkImportEmployees, isImporting } = useEmployeeCreateMutations();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // V14: 정상 결과 (부분 성공 포함) - 백엔드 V14가 항상 반환
  const [importResult, setImportResult] = useState<BulkImportResult | null>(null);
  // V14: 시스템 레벨 에러 (인증, 권한 실패만 - 백엔드 V14에서는 거의 발생 안 함)
  const [importError, setImportError] = useState<string | null>(null);
  // 파일 선택 에러 (별도)
  const [fileError, setFileError] = useState<string | null>(null);

  // ============================================
  // 이벤트 핸들러
  // ============================================

  /**
   * 파일 선택 핸들러 (V14)
   */
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 파일 형식 검증
    const validExtensions = ['.xlsx', '.xls'];
    const fileExtension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (!validExtensions.includes(fileExtension)) {
      setFileError('엑셀 파일(.xlsx 또는 .xls)만 업로드 가능합니다.');
      return;
    }

    setSelectedFile(file);
    setFileError(null);
    setImportResult(null);
    setImportError(null);
  };

  /**
   * 파일 업로드 및 파싱 핸들러 (V14)
   *
   * V14 변경사항:
   * - 백엔드 V14가 항상 BulkImportResult를 반환하므로 catch로 빠지는 경우 최소화
   * - 인증/권한 에러만 catch로 넘어옴
   * - "internal 에러" 대신 항상 상세 결과 표시
   */
  const handleUpload = async () => {
    if (!selectedFile) {
      setFileError('파일을 선택해주세요.');
      return;
    }

    // V17: 상태 초기화
    setFileError(null);
    setImportResult(null);
    setImportError(null);

    try {
      // 1. 파일 읽기
      const arrayBuffer = await selectedFile.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });

      // 2. 첫 번째 시트 선택
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];

      // 3. 시트를 2D 배열로 변환 (원시 데이터)
      const rawRows: any[][] = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: '',
        raw: false,
      });

      if (rawRows.length < 2) {
        setFileError('엑셀 파일에 데이터가 없습니다. (헤더 + 최소 1개 행 필요)');
        return;
      }

      // 4. Cloud Function 호출 (V17: 항상 결과 반환됨, internal 에러 없음)
      const result = await bulkImportEmployees({ rawRows });

      // V17: result가 있으면 항상 모달에 표시
      if (result) {
        setImportResult(result);

        // V63: onSuccess는 결과 확인 후 모달 닫기 시 호출 (자동 닫힘 방지)
        // 사용자가 결과를 읽고 닫기 버튼을 누를 때까지 팝업 유지
      } else {
        // V17: result가 null인 경우 (이론상 발생하지 않음)
        setImportError('서버에서 응답을 받지 못했습니다. 관리자에게 문의하세요.');
      }

    } catch (err: any) {
      // V17: 인증/권한 에러 처리
      if (err.code === 'unauthenticated') {
        setImportError('로그인이 필요합니다. 다시 로그인해주세요.');
      } else if (err.code === 'permission-denied') {
        setImportError('직원 엑셀 업로드 권한이 없습니다. 인사팀 또는 시스템 관리자에게 문의하세요.');
      } else {
        // V17: 그 외 에러 (이론상 발생하지 않음 - 프론트에서 처리됨)
        const errorMessage =
          err.details?.message ||
          err.message ||
          '직원 엑셀 업로드 중 알 수 없는 오류가 발생했습니다.';
        setImportError(errorMessage);
      }
    }
  };

  /**
   * 모달 닫기 핸들러 (V63: 성공 결과 있으면 onSuccess 호출)
   */
  const handleClose = () => {
    if (isImporting) return;

    // V63: 성공 건이 있었으면 닫기 시점에 onSuccess 호출 (목록 새로고침)
    const hadSuccess = importResult && importResult.successCount > 0;

    setSelectedFile(null);
    setImportResult(null);
    setImportError(null);
    setFileError(null);
    onClose();

    if (hadSuccess) {
      onSuccess();
    }
  };

  // V63: 결과 표시 중에는 오버레이 클릭으로 닫히지 않도록 보호
  const overlayProps = useModalOverlayClose(importResult ? undefined : handleClose);

  // ============================================
  // 렌더링
  // ============================================

  if (!isOpen) return null;

  return (
    <div style={overlayStyle} {...overlayProps}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* 헤더 */}
        <div style={headerStyle}>
          <h2 style={titleStyle}>엑셀 업로드로 직원 추가</h2>
          <button
            onClick={handleClose}
            disabled={isImporting}
            style={{
              ...closeButtonStyle,
              cursor: isImporting ? 'not-allowed' : 'pointer',
              opacity: isImporting ? 0.5 : 1,
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* 본문 */}
        <div style={bodyStyle}>
          {/* V14: 파일 선택 에러 */}
          {fileError && (
            <div style={errorBoxStyle}>
              <AlertCircle size={16} />
              <span>{fileError}</span>
            </div>
          )}

          {/* V14: 시스템 에러 (인증, 권한 실패 - 백엔드 V14에서는 거의 발생 안 함) */}
          {importError && (
            <div style={errorBoxStyle}>
              <AlertCircle size={16} />
              <span>{importError}</span>
            </div>
          )}

          {/* V14: 업로드 결과 (항상 표시 - 백엔드 V14가 항상 반환) */}
          {importResult && (
            <div style={resultBoxStyle}>
              <div style={resultHeaderStyle}>
                <CheckCircle size={20} color={importResult.successCount > 0 ? 'var(--color-success)' : 'var(--color-warning)'} />
                <span style={{ fontWeight: '600', fontSize: '16px' }}>
                  {importResult.failureCount === 0 ? '업로드 완료' : '업로드 완료 (일부 실패)'}
                </span>
              </div>
              <div style={resultSummaryStyle}>
                총 <strong>{importResult.totalCount}</strong>건 중{' '}
                <span style={{ color: 'var(--color-success)', fontWeight: '600' }}>
                  성공 {importResult.successCount}건
                </span>
                ,{' '}
                <span style={{ color: importResult.failureCount > 0 ? 'var(--color-error)' : 'inherit', fontWeight: '600' }}>
                  실패 {importResult.failureCount}건
                </span>
              </div>

              {/* V14: 실패 목록 */}
              {importResult.failures && importResult.failures.length > 0 && (
                <div style={failureListStyle}>
                  <div style={{ fontWeight: '600', marginBottom: '8px', color: 'var(--color-error)' }}>
                    실패 상세:
                  </div>
                  <div style={failureItemsContainerStyle}>
                    {importResult.failures.map((failure, index) => (
                      <div key={index} style={failureItemStyle}>
                        <span style={{ fontWeight: '500' }}>행 {failure.rowIndex}:</span>
                        <span>{failure.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 파일 선택 영역 */}
          {!importResult && (
            <>
              <div style={instructionBoxStyle}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600' }}>
                  📋 안내사항
                </h3>
                <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.6' }}>
                  <li>엑셀 파일 형식: .xlsx 또는 .xls</li>
                  <li>첫 번째 행은 헤더로 사용됩니다</li>
                  <li>
                    필수 컬럼: 이름, 이메일, 사번, 회사, 직위, 직책, 정규입사일 또는 파견입사일
                  </li>
                  <li>주민등록번호는 선택 사항입니다</li>
                </ul>
              </div>

              <div style={fileInputContainerStyle}>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  disabled={isImporting}
                  style={{ display: 'none' }}
                  id="excel-file-input"
                />
                <label htmlFor="excel-file-input" style={fileInputLabelStyle}>
                  <FileSpreadsheet size={24} />
                  <span>{selectedFile ? selectedFile.name : '엑셀 파일 선택'}</span>
                </label>
              </div>
            </>
          )}

          {/* 버튼 그룹 (V14: importResult 사용) */}
          <div style={buttonGroupStyle}>
            {importResult ? (
              <button onClick={handleClose} style={closeResultButtonStyle}>
                닫기
              </button>
            ) : (
              <>
                <button
                  onClick={handleClose}
                  disabled={isImporting}
                  style={{
                    ...cancelButtonStyle,
                    cursor: isImporting ? 'not-allowed' : 'pointer',
                    opacity: isImporting ? 0.5 : 1,
                  }}
                >
                  취소
                </button>
                <button
                  onClick={handleUpload}
                  disabled={!selectedFile || isImporting}
                  style={{
                    ...uploadButtonStyle,
                    cursor: !selectedFile || isImporting ? 'not-allowed' : 'pointer',
                    opacity: !selectedFile || isImporting ? 0.5 : 1,
                  }}
                >
                  {isImporting ? (
                    <>
                      <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      <span>업로드 중...</span>
                    </>
                  ) : (
                    <>
                      <Upload size={16} />
                      <span>업로드</span>
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// 스타일 정의
// ============================================

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  background: 'white',
  borderRadius: '12px',
  width: '90%',
  maxWidth: '700px',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
};

const headerStyle: React.CSSProperties = {
  padding: '20px 24px',
  borderBottom: '1px solid var(--color-gray-200)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const titleStyle: React.CSSProperties = {
  fontSize: '20px',
  fontWeight: '600',
  color: 'var(--color-gray-800)',
  margin: 0,
};

const closeButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: '8px',
  cursor: 'pointer',
  color: 'var(--color-gray-600)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '4px',
};

const bodyStyle: React.CSSProperties = {
  padding: '24px',
};

const errorBoxStyle: React.CSSProperties = {
  marginBottom: '16px',
  padding: '12px',
  background: 'var(--color-error-light, #fee)',
  border: '1px solid var(--color-error)',
  borderRadius: '6px',
  color: 'var(--color-error)',
  fontSize: '14px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const instructionBoxStyle: React.CSSProperties = {
  padding: '16px',
  background: 'var(--color-gray-50)',
  border: '1px solid var(--color-gray-200)',
  borderRadius: '8px',
  fontSize: '13px',
  color: 'var(--color-gray-700)',
  marginBottom: '20px',
};

const fileInputContainerStyle: React.CSSProperties = {
  marginBottom: '20px',
};

const fileInputLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '16px 20px',
  background: 'var(--color-gray-50)',
  border: '2px dashed var(--color-gray-300)',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s',
  fontSize: '14px',
  color: 'var(--color-gray-700)',
  fontWeight: '500',
};

const resultBoxStyle: React.CSSProperties = {
  padding: '20px',
  background: 'var(--color-gray-50)',
  border: '1px solid var(--color-gray-200)',
  borderRadius: '8px',
  marginBottom: '20px',
};

const resultHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '12px',
};

const resultSummaryStyle: React.CSSProperties = {
  fontSize: '15px',
  color: 'var(--color-gray-700)',
  lineHeight: '1.5',
};

const failureListStyle: React.CSSProperties = {
  marginTop: '16px',
  paddingTop: '16px',
  borderTop: '1px solid var(--color-gray-200)',
  fontSize: '13px',
};

const failureItemsContainerStyle: React.CSSProperties = {
  maxHeight: '200px',
  overflow: 'auto',
};

const failureItemStyle: React.CSSProperties = {
  display: 'flex',
  gap: '8px',
  padding: '6px 0',
  color: 'var(--color-gray-700)',
};

const buttonGroupStyle: React.CSSProperties = {
  display: 'flex',
  gap: '12px',
  justifyContent: 'flex-end',
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '10px 20px',
  background: 'white',
  border: '1px solid var(--color-gray-300)',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
  color: 'var(--color-gray-700)',
  cursor: 'pointer',
};

const uploadButtonStyle: React.CSSProperties = {
  padding: '10px 20px',
  background: 'var(--color-primary)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
  color: 'white',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
};

const closeResultButtonStyle: React.CSSProperties = {
  padding: '10px 20px',
  background: 'var(--color-primary)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
  color: 'white',
  cursor: 'pointer',
};
